/**
 * Accounts.
 *
 * Small on purpose: an Admin makes an account, picks a role, and can reset a
 * password or switch the account off. There is no sign-up and no reset-by-email
 * — both are doors, and a panel that can change a clinic's website should have
 * as few of those as it can manage.
 *
 * Switching an account off ends the session it already has, so "disabled" means
 * disabled now rather than in twelve hours. The server enforces that, and also
 * refuses to leave the clinic with no Admin at all.
 */
import React, { useCallback, useEffect, useState } from "react";

import { ApiError, api } from "../api.js";
import { Message } from "../components/bits.jsx";

const ROLE_HELP = {
  editor: "Writes and edits drafts, uploads pictures, opens previews, and asks a Publisher to publish.",
  publisher: "Everything an Editor can, and publishes, unpublishes, edits search settings and the home page.",
  admin: "Everything, and manages these accounts.",
};

export default function Users({ me }) {
  const [users, setUsers] = useState(null);
  const [state, setState] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      setUsers((await api.get("users")).users);
    } catch (problem) {
      setState({ level: "error", text: problem instanceof ApiError ? problem.message : "Could not load the accounts." });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function patch(username, body, done) {
    setState(null);
    try {
      await api.patch(`users/${username}`, body);
      await load();
      if (done) setState({ level: "ok", text: done });
    } catch (problem) {
      setState({ level: "error", text: problem instanceof ApiError ? problem.message : "Could not change it." });
    }
  }

  return (
    <>
      <div className="head">
        <div>
          <h1>Accounts</h1>
          <p className="sub">Everyone who can sign in to the panel.</p>
        </div>
        <button className="primary" onClick={() => setAdding((v) => !v)}>
          Add somebody
        </button>
      </div>

      {state && <Message level={state.level}>{state.text}</Message>}

      {adding && <NewUser onDone={() => { setAdding(false); load(); }} onError={(text) => setState({ level: "error", text })} />}

      <div className="card">
        <div className="body" style={{ padding: 0 }}>
          {!users ? (
            <div className="empty">Loading…</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.username}>
                    <td>
                      <strong>{user.displayName}</strong>
                      {user.username === me.username && <span className="hint"> · you</span>}
                      <div className="hint mono">{user.username}{user.email ? ` · ${user.email}` : ""}</div>
                    </td>
                    <td>
                      <select
                        value={user.role}
                        aria-label={`Role for ${user.displayName}`}
                        style={{ width: "auto" }}
                        onChange={(e) => patch(user.username, { role: e.target.value }, `${user.displayName} is now ${e.target.value === "admin" ? "an" : "a"} ${e.target.value}.`)}
                      >
                        {Object.keys(ROLE_HELP).map((role) => (
                          <option key={role} value={role}>
                            {role}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <span className={`pill ${user.disabled ? "archived" : "published"}`}>{user.disabled ? "off" : "active"}</span>
                    </td>
                    <td>
                      <div className="row">
                        <button
                          className="quiet"
                          onClick={() => {
                            const password = window.prompt(`A new password for ${user.displayName}.\n\nAt least 12 characters. Tell it to them in person or by phone, not by email.`);
                            if (password) patch(user.username, { password }, `${user.displayName}'s password is changed, and they have been signed out.`);
                          }}
                        >
                          Reset password
                        </button>
                        <button
                          className={user.disabled ? "quiet" : "danger"}
                          onClick={() =>
                            patch(
                              user.username,
                              { disabled: !user.disabled },
                              user.disabled ? `${user.displayName} can sign in again.` : `${user.displayName} is switched off and signed out.`
                            )
                          }
                        >
                          {user.disabled ? "Switch on" : "Switch off"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="card">
        <header>
          <h2>What each role can do</h2>
        </header>
        <div className="body">
          <dl style={{ margin: 0 }}>
            {Object.entries(ROLE_HELP).map(([role, help]) => (
              <div key={role} style={{ marginBottom: "0.6rem" }}>
                <dt style={{ fontWeight: 650, textTransform: "capitalize" }}>{role}</dt>
                <dd style={{ margin: 0, color: "var(--ink-soft)" }}>{help}</dd>
              </div>
            ))}
          </dl>
          <p className="hint">
            Give each person their own account rather than sharing one. The history then shows who
            changed what, and somebody leaving means switching off one account instead of telling
            everyone a new password.
          </p>
        </div>
      </div>
    </>
  );
}

function NewUser({ onDone, onError }) {
  const [form, setForm] = useState({ username: "", displayName: "", email: "", role: "editor", password: "" });
  const [busy, setBusy] = useState(false);

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    try {
      await api.post("users", form);
      onDone();
    } catch (problem) {
      onError(problem instanceof ApiError ? problem.message : "Could not make the account.");
      setBusy(false);
    }
  }

  const field = (key, label, type = "text", help) => (
    <div className="field">
      <label htmlFor={`u-${key}`}>{label}</label>
      <input
        id={`u-${key}`}
        type={type}
        value={form[key]}
        required={key !== "email"}
        autoComplete={key === "password" ? "new-password" : "off"}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
      />
      {help && <p className="help">{help}</p>}
    </div>
  );

  return (
    <form className="card" onSubmit={submit}>
      <header>
        <h2>A new account</h2>
      </header>
      <div className="body">
        {field("displayName", "Their name", "text", "Shown in the panel and recorded against everything they change.")}
        {field("username", "Username", "text", "Lower case, no spaces.")}
        {field("email", "Email (optional)", "email", "Recorded with their changes. The panel never sends email.")}
        <div className="field">
          <label htmlFor="u-role">Role</label>
          <select id="u-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            {Object.keys(ROLE_HELP).map((role) => (
              <option key={role} value={role}>
                {role}
              </option>
            ))}
          </select>
          <p className="help">{ROLE_HELP[form.role]}</p>
        </div>
        {field("password", "First password", "password", "At least 12 characters. Tell it to them in person or by phone — they can change it once they are in.")}
        <button className="primary" disabled={busy}>
          {busy ? "Making it…" : "Make the account"}
        </button>
      </div>
    </form>
  );
}
